import React, { useState, useEffect, useContext } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import RefreshIcon from "@material-ui/icons/Refresh";
import StorageIcon from "@material-ui/icons/Storage";
import MemoryIcon from "@material-ui/icons/Memory";
import DnsIcon from "@material-ui/icons/Dns";
import SpeedIcon from "@material-ui/icons/Speed";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		padding: theme.spacing(4, 4, 3),
	},
	container: {
		maxWidth: 800,
	},
	paper: {
		padding: theme.spacing(2.5),
		borderRadius: 10,
		border: "1px solid #e2e8f0",
		marginBottom: 16,
		backgroundColor: "#ffffff",
	},
	settingOption: {
		marginLeft: "auto",
	},
	healthCheckHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		width: "100%",
		marginBottom: 16,
	},
	quotaCard: {
		padding: theme.spacing(1.8),
		backgroundColor: "#f8fafc",
		border: "1px solid #e2e8f0",
		borderRadius: 8,
		display: "flex",
		flexDirection: "column",
		gap: 8,
	},
	quotaTitle: {
		fontSize: "0.82rem",
		fontWeight: 700,
		color: "#334155",
		display: "flex",
		alignItems: "center",
		gap: 6,
	},
	quotaValue: {
		fontSize: "1.1rem",
		fontWeight: 800,
		color: "#0f172a",
	},
	quotaSub: {
		fontSize: "0.72rem",
		color: "#64748b",
	},
	progressBar: {
		height: 8,
		borderRadius: 4,
		backgroundColor: "#e2e8f0",
		"& .MuiLinearProgress-bar": {
			borderRadius: 4,
		},
	},
	checkGridItem: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "8px 12px",
		backgroundColor: "#f8fafc",
		border: "1px solid #e2e8f0",
		borderRadius: 6,
	},
	statusBadge: {
		fontSize: "0.75rem",
		fontWeight: 700,
		padding: "3px 8px",
		borderRadius: 6,
	},
}));

const Settings = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const isAdmin = user?.profile === "admin";

	const [settings, setSettings] = useState([]);
	const [healthCheck, setHealthCheck] = useState(null);
	const [healthCheckLoading, setHealthCheckLoading] = useState(false);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					aux[settingIndex].value = data.setting.value;
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting?.value;
	};

	const handleRunHealthCheck = async () => {
		setHealthCheckLoading(true);
		try {
			const { data } = await api.get("/health-check");
			setHealthCheck(data);
		} catch (err) {
			toastError(err);
		} finally {
			setHealthCheckLoading(false);
		}
	};

	const getProgressColor = pct => {
		if (pct >= 90) return "#dc2626"; // Vermelho
		if (pct >= 70) return "#d97706"; // Laranja/Amarelo
		return "#16a34a"; // Verde
	};

	return (
		<div className={classes.root}>
			<Container className={classes.container}>
				<Typography variant="h6" style={{ fontWeight: 800, color: "#0f172a", marginBottom: 16 }}>
					⚙️ Configurações & Infraestrutura
				</Typography>

				{/* 1. MONITORAMENTO GERAL E COTAS (ADMIN) */}
				{isAdmin && (
					<Paper className={classes.paper} variant="outlined">
						<div className={classes.healthCheckHeader}>
							<div>
								<Typography variant="subtitle1" style={{ fontWeight: 800, color: "#0f172a" }}>
									🛡️ Monitor de Saúde & Capacidade do Sistema
								</Typography>
								<Typography variant="caption" style={{ color: "#64748b" }}>
									Verificação em tempo real de infraestrutura, limites do banco, servidor e IA
								</Typography>
							</div>

							<Button
								variant="contained"
								color="primary"
								size="small"
								disabled={healthCheckLoading}
								onClick={handleRunHealthCheck}
								startIcon={healthCheckLoading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
								style={{ textTransform: "none", fontWeight: 700 }}
							>
								{healthCheckLoading ? "Verificando..." : "Rodar Verificação"}
							</Button>
						</div>

						{healthCheck && (
							<>
								<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, padding: "8px 12px", backgroundColor: healthCheck.allOk ? "#f0fdf4" : "#fef2f2", border: `1px solid ${healthCheck.allOk ? "#bbf7d0" : "#fecaca"}`, borderRadius: 8 }}>
									<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
										{healthCheck.allOk ? (
											<CheckCircleIcon style={{ color: "#16a34a" }} />
										) : (
											<CancelIcon style={{ color: "#dc2626" }} />
										)}
										<span style={{ fontWeight: 800, fontSize: "0.88rem", color: healthCheck.allOk ? "#166534" : "#991b1b" }}>
											{healthCheck.allOk ? "🟢 Todos os sistemas operando perfeitamente e dentro das cotas" : "⚠️ Algum serviço precisa de atenção"}
										</span>
									</div>
									<span style={{ fontSize: "0.75rem", color: "#64748b" }}>
										Último teste: {new Date(healthCheck.checkedAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
									</span>
								</div>

								{/* BARRAS DE COTAS E CAPACIDADE */}
								{healthCheck.quotas && (
									<>
										<Typography variant="caption" style={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, display: "block" }}>
											📊 Capacidade & Limites de Armazenamento
										</Typography>

										<Grid container spacing={2} style={{ marginBottom: 18 }}>
											{/* 1. BANCO POSTGRESQL LOCAL NA VPS */}
											<Grid item xs={12} sm={4}>
												<div className={classes.quotaCard}>
													<div className={classes.quotaTitle}>
														<StorageIcon fontSize="small" style={{ color: "#0284c7" }} />
														<span>{healthCheck.quotas.database.label || "Postgres 17 (VPS Oracle)"}</span>
													</div>
													<div className={classes.quotaValue}>
														{healthCheck.quotas.database.usedFormatted}{" "}
														<span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "normal" }}>
															/ {healthCheck.quotas.database.totalFormatted} ({healthCheck.quotas.database.percentage}%)
														</span>
													</div>
													<LinearProgress
														variant="determinate"
														value={healthCheck.quotas.database.percentage}
														className={classes.progressBar}
														style={{
															"& .MuiLinearProgress-bar": {
																backgroundColor: getProgressColor(healthCheck.quotas.database.percentage)
															}
														}}
													/>
													<div className={classes.quotaSub}>
														{healthCheck.quotas.database.detail}
													</div>
												</div>
											</Grid>

											{/* 2. DISCO VPS */}
											<Grid item xs={12} sm={4}>
												<div className={classes.quotaCard}>
													<div className={classes.quotaTitle}>
														<DnsIcon fontSize="small" style={{ color: "#7c3aed" }} />
														<span>Disco da VPS (Oracle)</span>
													</div>
													<div className={classes.quotaValue}>
														{healthCheck.quotas.disk.usedFormatted}{" "}
														<span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "normal" }}>
															/ {healthCheck.quotas.disk.totalFormatted} ({healthCheck.quotas.disk.percentage}%)
														</span>
													</div>
													<LinearProgress
														variant="determinate"
														value={healthCheck.quotas.disk.percentage}
														className={classes.progressBar}
													/>
													<div className={classes.quotaSub}>
														{healthCheck.quotas.disk.detail}
													</div>
												</div>
											</Grid>

											{/* 3. MEMÓRIA RAM */}
											<Grid item xs={12} sm={4}>
												<div className={classes.quotaCard}>
													<div className={classes.quotaTitle}>
														<MemoryIcon fontSize="small" style={{ color: "#16a34a" }} />
														<span>Memória RAM (VPS)</span>
													</div>
													<div className={classes.quotaValue}>
														{healthCheck.quotas.ram.usedFormatted}{" "}
														<span style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: "normal" }}>
															/ {healthCheck.quotas.ram.totalFormatted} ({healthCheck.quotas.ram.percentage}%)
														</span>
													</div>
													<LinearProgress
														variant="determinate"
														value={healthCheck.quotas.ram.percentage}
														className={classes.progressBar}
													/>
													<div className={classes.quotaSub}>
														{healthCheck.quotas.ram.detail}
													</div>
												</div>
											</Grid>
										</Grid>
									</>
								)}

								{/* GRID DE CHECKS DOS SERVIÇOS */}
								<Typography variant="caption" style={{ fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8, display: "block" }}>
									⚡ Tempo de Resposta e Conexões
								</Typography>

								<Grid container spacing={1}>
									{healthCheck.results.map((result, idx) => (
										<Grid item xs={12} sm={6} key={idx}>
											<div className={classes.checkGridItem}>
												<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
													{result.ok ? (
														<CheckCircleIcon style={{ color: "#16a34a", fontSize: 18 }} />
													) : (
														<CancelIcon style={{ color: "#dc2626", fontSize: 18 }} />
													)}
													<div>
														<div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#1e293b" }}>
															{result.label}
														</div>
														<div style={{ fontSize: "0.72rem", color: "#64748b" }}>
															{result.detail}
														</div>
													</div>
												</div>
												{result.ms !== undefined && (
													<span style={{ fontSize: "0.7rem", fontWeight: 700, backgroundColor: "#e2e8f0", color: "#334155", padding: "2px 6px", borderRadius: 4 }}>
														{result.ms}ms
													</span>
												)}
											</div>
										</Grid>
									))}
								</Grid>
							</>
						)}
					</Paper>
				)}

				{/* 2. CONFIGURAÇÕES GERAIS */}
				<Paper className={classes.paper} variant="outlined" style={{ display: "flex", alignItems: "center" }}>
					<Typography variant="body2" style={{ fontWeight: 600, color: "#334155" }}>
						{i18n.t("settings.settings.userCreation.name")}
					</Typography>
					<Select
						margin="dense"
						variant="outlined"
						native
						id="userCreation-setting"
						name="userCreation"
						value={
							settings && settings.length > 0 && getSettingValue("userCreation")
						}
						className={classes.settingOption}
						onChange={handleChangeSetting}
					>
						<option value="enabled">
							{i18n.t("settings.settings.userCreation.options.enabled")}
						</option>
						<option value="disabled">
							{i18n.t("settings.settings.userCreation.options.disabled")}
						</option>
					</Select>
				</Paper>

				<Paper className={classes.paper} variant="outlined">
					<TextField
						id="api-token-setting"
						readonly
						label="Token API"
						margin="dense"
						variant="outlined"
						fullWidth
						value={settings && settings.length > 0 && getSettingValue("userApiToken")}
					/>
				</Paper>
			</Container>
		</div>
	);
};

export default Settings;
